// ════════════════════════════════════════════════════════════════════════════
// FRONTEND COMPONENT REGISTRY
// ════════════════════════════════════════════════════════════════════════════
/**
 * Registry for frontend plugin components.
 * Separate from the main registry to allow tree-shaking in backend-only builds.
 */
const frontendRegistry = new Map();
/**
 * Register frontend components for a plugin.
 * @param slug - The plugin's unique identifier
 * @param components - The frontend components to register
 * @example
 * ```typescript
 * import { registerFrontendPlugin } from "@unios/action-plugins/frontend";
 * import { Icon, ConfigEditor, ExecutionView } from "./components";
 *
 * registerFrontendPlugin("input-form", {
 *   Icon,
 *   ConfigEditor,
 *   ExecutionView,
 * });
 * ```
 */
export function registerFrontendPlugin(slug, components) {
    if (frontendRegistry.has(slug)) {
        console.warn(`Frontend components for plugin "${slug}" are being overwritten. ` +
            `This may indicate a duplicate registration.`);
    }
    frontendRegistry.set(slug, components);
}
/**
 * Get frontend components for a plugin.
 * @param slug - The plugin's unique identifier
 * @returns The components if registered, undefined otherwise
 */
export function getFrontendPlugin(slug) {
    return frontendRegistry.get(slug);
}
/**
 * Get the Icon component for a plugin.
 * @param slug - The plugin's unique identifier
 * @returns The Icon component if registered, undefined otherwise
 */
export function getPluginIcon(slug) {
    return frontendRegistry.get(slug)?.Icon;
}
/**
 * Get all registered plugin slugs with frontend components.
 * @returns Array of plugin slugs
 */
export function getFrontendPluginSlugs() {
    return Array.from(frontendRegistry.keys());
}
/**
 * Check if a plugin has registered frontend components.
 * @param slug - The plugin's unique identifier
 * @returns true if frontend components are registered
 */
export function hasFrontendPlugin(slug) {
    return frontendRegistry.has(slug);
}
/**
 * Clear all registered frontend components.
 * Primarily useful for testing.
 */
export function clearFrontendPlugins() {
    frontendRegistry.clear();
}
// ════════════════════════════════════════════════════════════════════════════
// ICON FALLBACK MAPPING
// ════════════════════════════════════════════════════════════════════════════
/**
 * Fallback icon mapping by base action type.
 * Used when a plugin doesn't have a registered Icon component.
 */
const fallbackIconComponents = new Map();
/**
 * Register a fallback icon for a base action type.
 * @param type - The base action type ("comment", "input", "automation", "link")
 * @param Icon - The icon component to use as fallback
 */
export function registerFallbackIcon(type, Icon) {
    fallbackIconComponents.set(type, Icon);
}
/**
 * Get a fallback icon for a base action type.
 * @param type - The base action type
 * @returns The fallback icon component if registered
 */
export function getFallbackIcon(type) {
    return fallbackIconComponents.get(type);
}
/**
 * Get the icon for an action type, with fallback support.
 * @param slug - The plugin's unique identifier
 * @param type - The base action type (for fallback)
 * @returns The icon component (plugin-specific, type fallback, or undefined)
 */
export function getActionIcon(slug, type) {
    // First try plugin-specific icon
    const pluginIcon = getPluginIcon(slug);
    if (pluginIcon) {
        return pluginIcon;
    }
    // Then try type-based fallback
    if (type) {
        return getFallbackIcon(type);
    }
    return undefined;
}
//# sourceMappingURL=registry.js.map